// ====================================================================
// COLLISION — uniform spatial hash over static obstacle AABBs
// ====================================================================
// Replaces the "linear scan of one global obstacles[] array, 3×/frame"
// pattern. Fine at ~200 boxes, fatal at 10,000 — and the chunked world is
// headed for thousands. Obstacles register once at build time; queries walk
// only the grid cells the query box covers.
//
// Obstacles are static (props don't move), so there's no remove/update path
// yet — chunk *visuals* stream in and out, but collision stays resident
// (1,000 AABBs ≈ 50 KB; not worth the complexity of streaming until zones
// multiply the world. Revisit when they do.)
const CELL = 4;                       // units per grid cell
const grid = new Map();               // cellKey -> obstacle[]

export const obstacles = [];          // master list (debug/telemetry)

const keyOf = (cx, cz) => (cx + 512) * 2048 + (cz + 512);

// Insert an obstacle into every grid cell its AABB overlaps. If ob._cells is an
// array, the cell keys are recorded there so a movable obstacle can later be
// pulled back out — static obstacles leave it undefined and pay nothing.
function insert(ob) {
  const cx0 = Math.floor(ob.minX / CELL), cx1 = Math.floor(ob.maxX / CELL);
  const cz0 = Math.floor(ob.minZ / CELL), cz1 = Math.floor(ob.maxZ / CELL);
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cz = cz0; cz <= cz1; cz++) {
      const k = keyOf(cx, cz);
      let bucket = grid.get(k);
      if (!bucket) { bucket = []; grid.set(k, bucket); }
      bucket.push(ob);
      if (ob._cells) ob._cells.push(k);
    }
  }
}

function remove(ob) {
  if (!ob._cells) return;
  for (const k of ob._cells) {
    const bucket = grid.get(k);
    if (bucket) { const i = bucket.indexOf(ob); if (i >= 0) bucket.splice(i, 1); }
  }
  ob._cells.length = 0;
}

// Optional capture sink: while open, addObstacle creates MOVABLE obstacles and
// collects them, so captureStructure() (main.js) can let a dragged building take
// its collision with it. No effect on the static fast path when closed.
let _capture = null;
export function beginObstacleCapture() { _capture = []; }
export function endObstacleCapture() { const c = _capture || []; _capture = null; return c; }

export function addObstacle(ob) {
  // ob: { minX, maxX, minY, maxY, minZ, maxZ }
  ob._stamp = 0;
  if (_capture) { ob._cells = []; _capture.push(ob); }
  obstacles.push(ob);
  insert(ob);
  return ob;
}

// ---- Movable obstacles (editor only) ------------------------------------
// The world is overwhelmingly static, so addObstacle has no remove path by
// design. The building editor needs a small set of obstacles that follow a
// dragged structure: these track their grid cells so they can be re-bucketed.
export function addMovableObstacle(ob) {
  ob._stamp = 0;
  ob._cells = [];
  obstacles.push(ob);
  insert(ob);
  return ob;
}

// Re-position a movable obstacle: pull it from its old cells, overwrite its
// AABB, re-insert. aabb is { minX, maxX, minY, maxY, minZ, maxZ }.
export function updateMovableObstacle(ob, aabb) {
  remove(ob);
  ob.minX = aabb.minX; ob.maxX = aabb.maxX;
  ob.minY = aabb.minY; ob.maxY = aabb.maxY;
  ob.minZ = aabb.minZ; ob.maxZ = aabb.maxZ;
  insert(ob);
}

export function removeMovableObstacle(ob) {
  remove(ob);
  const i = obstacles.indexOf(ob);
  if (i >= 0) obstacles.splice(i, 1);
}

// Collect obstacles whose cells intersect the query box into `out` (a reused
// array — zero allocation per frame). Returns the count; entries past it are
// stale. Obstacles spanning several cells are deduped with a query stamp.
let _queryId = 0;
export function collectObstaclesNear(minX, minZ, maxX, maxZ, out) {
  _queryId++;
  let n = 0;
  const cx0 = Math.floor(minX / CELL), cx1 = Math.floor(maxX / CELL);
  const cz0 = Math.floor(minZ / CELL), cz1 = Math.floor(maxZ / CELL);
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = grid.get(keyOf(cx, cz));
      if (!bucket) continue;
      for (const ob of bucket) {
        if (ob._stamp === _queryId) continue;
        ob._stamp = _queryId;
        out[n++] = ob;
      }
    }
  }
  return n;
}

// Reused scratch for raycastObstacles — keeps the cast zero-allocation.
const _segObs = [];

// Cast a ray from (ox,oy,oz) along the UNIT direction (dx,dy,dz) up to maxDist
// and return the nearest obstacle entry distance, or maxDist if nothing blocks.
// The camera pull-in uses this to dolly in when a structure blocks the view of
// Chocky. dir must be normalized so the slab `t` values are true world units.
// Zero allocation: one hash query over the segment's XZ box, then a 3D slab
// test per candidate. AABB Y bounds are honoured, so a high camera passes over
// short obstacles (rocks, fences) without falsely pulling in.
export function raycastObstacles(ox, oy, oz, dx, dy, dz, maxDist) {
  const ex = ox + dx * maxDist, ez = oz + dz * maxDist;
  // Pad the XZ query so steep/near-vertical camera rays still test nearby
  // tall blockers; the exact 3D slab test below filters the extras.
  const QUERY_PAD = 0.75;
  const minX = Math.min(ox, ex) - QUERY_PAD, maxX = Math.max(ox, ex) + QUERY_PAD;
  const minZ = Math.min(oz, ez) - QUERY_PAD, maxZ = Math.max(oz, ez) + QUERY_PAD;
  const n = collectObstaclesNear(minX, minZ, maxX, maxZ, _segObs);
  let nearest = maxDist;
  for (let i = 0; i < n; i++) {
    const ob = _segObs[i];
    let t0 = 0, t1 = nearest;
    // X / Y / Z slabs. Parallel-and-outside on any axis is a miss.
    if (Math.abs(dx) < 1e-8) { if (ox < ob.minX || ox > ob.maxX) continue; }
    else { const inv = 1 / dx; let tn = (ob.minX - ox) * inv, tf = (ob.maxX - ox) * inv; if (tn > tf) { const s = tn; tn = tf; tf = s; } if (tn > t0) t0 = tn; if (tf < t1) t1 = tf; if (t0 > t1) continue; }
    if (Math.abs(dy) < 1e-8) { if (oy < ob.minY || oy > ob.maxY) continue; }
    else { const inv = 1 / dy; let tn = (ob.minY - oy) * inv, tf = (ob.maxY - oy) * inv; if (tn > tf) { const s = tn; tn = tf; tf = s; } if (tn > t0) t0 = tn; if (tf < t1) t1 = tf; if (t0 > t1) continue; }
    if (Math.abs(dz) < 1e-8) { if (oz < ob.minZ || oz > ob.maxZ) continue; }
    else { const inv = 1 / dz; let tn = (ob.minZ - oz) * inv, tf = (ob.maxZ - oz) * inv; if (tn > tf) { const s = tn; tn = tf; tf = s; } if (tn > t0) t0 = tn; if (tf < t1) t1 = tf; if (t0 > t1) continue; }
    if (t0 >= 0 && t0 < nearest) nearest = t0;
  }
  return nearest;
}
// Raycast standable obstacle tops. Used by tap-to-move so taps on roofs and
// platforms choose the visible walkable surface instead of falling through to
// terrain underneath. Returns true and writes the nearest hit to `out`, or false
// if no upward-facing top is hit.
export function raycastObstacleTops(ox, oy, oz, dx, dy, dz, maxDist, out) {
  if (Math.abs(dy) < 1e-8) return false;
  const ex = ox + dx * maxDist, ez = oz + dz * maxDist;
  const QUERY_PAD = 0.75;
  const minX = Math.min(ox, ex) - QUERY_PAD, maxX = Math.max(ox, ex) + QUERY_PAD;
  const minZ = Math.min(oz, ez) - QUERY_PAD, maxZ = Math.max(oz, ez) + QUERY_PAD;
  const n = collectObstaclesNear(minX, minZ, maxX, maxZ, _segObs);
  let nearest = maxDist;
  let hit = false;
  for (let i = 0; i < n; i++) {
    const ob = _segObs[i];
    const t = (ob.maxY - oy) / dy;
    if (t < 0 || t >= nearest || t > maxDist) continue;
    const x = ox + dx * t;
    const z = oz + dz * t;
    if (x < ob.minX || x > ob.maxX || z < ob.minZ || z > ob.maxZ) continue;
    nearest = t;
    out.set(x, ob.maxY, z);
    hit = true;
  }
  return hit;
}
